import { DeliveryStatus } from "@types_def/models/delivery.types";

/** Client may only self-reactivate within this window after cancellation (see product rule below). */
export const CLIENT_DELIVERY_REACTIVATE_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * Whether a **non-admin (client)** may call `updateDeliveryStatus` to move a cancelled delivery back
 * to `REQUESTED` (“reactivate” / “activate”).
 *
 * **Product / security rule:** A client must not be able to revert arbitrary cancellations (for example
 * admin cancel, mandatory cancel, or a customer cancel that already progressed past the initial
 * requested phase). Reactivation is allowed only when **all** of the following hold:
 *
 * 1. Status is `CANCELLED_BY_CUSTOMER` (the customer-initiated cancel path).
 * 2. `cancelledAt` is set and is within {@link CLIENT_DELIVERY_REACTIVATE_WINDOW_MS} (one day).
 * 3. `smsSentAt` is unset — we treat that as “still in the REQUESTED-phase funnel” (once an SMS is
 *    sent, the flow moves to `PENDING_CUSTOMER_CONFIRMATION` / related states; those cancels must not
 *    be undoable by the client from this action).
 *
 * **Backend:** This must be duplicated in the API / `updateDeliveryStatus` resolver. The UI alone
 * cannot provide security; clients can always craft GraphQL requests.
 */
export function canClientReactivateCancelledDelivery(input: {
  status: DeliveryStatus;
  cancelledAt: Date | string | null | undefined;
  smsSentAt: Date | string | null | undefined;
}): boolean {
  if (input.status !== DeliveryStatus.CANCELLED_BY_CUSTOMER) {
    return false;
  }
  if (input.smsSentAt != null) {
    return false;
  }
  if (input.cancelledAt == null) {
    return false;
  }
  const cancelled =
    input.cancelledAt instanceof Date ? input.cancelledAt : new Date(input.cancelledAt);
  if (Number.isNaN(cancelled.getTime())) {
    return false;
  }
  return Date.now() - cancelled.getTime() <= CLIENT_DELIVERY_REACTIVATE_WINDOW_MS;
}
